import {h, Component, ComponentChild} from 'preact'
import qs from 'qs'
import axios, {AxiosError, AxiosResponse} from 'axios'
import globals from '../../utils/globals'
import Header from './Header'
import Loader from '../ui-components/Loader'
import OverviewFilters from './filters/OverviewFilters'
import OverviewColumns, {copyColumns} from './columns/OverviewColumns'
import Table from '../table/Table'
import EmptyPage from '../empty-page/EmptyPage'
import SaveCustomViewModal from '../moderation-overview/SaveCustomViewModal'
import AlertComponent from '../ui-components/AlertComponent'
import copyToClipboard from '../utils/copyToClipboard'
import tracker from '../../components/tracker/publish'
import {Model} from "../table/types";
import Icon from "../assets";
import ButtonControl from "../button-control";
import {CustomButton} from "../header";

export type Filters = {
  [filter: string]: Array<any> | string | any
}

export type ViewColumn = {
  accessor: string,
  isHidden: boolean
}

export type FilterSelector = {
  id: string,
  type: string,
  name?: string,
  component?: Function,
  items?: Array<any>,
  isSearchable?: boolean,
  fetchUrl?: string,
  isMain?: boolean,
}

type Sort = {
  [key: string]: string,
}

type Props = {
  type: string,
  headerTitle: string,
  headerButtons?: Array<CustomButton | ComponentChild>,
  withFilters?: Array<FilterSelector>,
  withSearch?: boolean,
  searchPlaceholder?: string,
  withSorting?: Sort,
  withPagination?: {
    page: number,
    pageSize: number
  },
  withColumns?: boolean,
  rowId: string,
  endpoint: string,
  dataKey: string,
  model: Model[],
  EmptyStateComponent: JSX.Element
}

type State = {
  data: [],
  total?: string,
  loading: boolean,
  areFiltersApplied: boolean,
  filters?: Filters,
  pagination?: {
    page: number,
    pageSize: number
  },
  sort?: Sort,
  columns: Model[],
  [key: string]: any,
  showSaveCustomViewModal: boolean,
  customViewInputNameModal: string,
  customViewInputNameTitle: string,
  customViewInputNameError: string,
  showAlert: boolean,
  alertMessage: string,
  alertType: string|null,
  alertVisibleFor: number|null,
  customViewId?: number,
  customViewRadioInput: string
}

const createNewCustomView = 'Create new custom view'
const updateExistingCustomView = 'Save changes to the current view'

class Overview extends Component<Props, State> {
  private STORAGE_KEY = `${this.props.type}-filter-criteria`
  private defaultCdn = globals('defaultCdn')

  constructor (props: Props) {
    super(props)
    this.state = {
      data: [],
      total: undefined,
      loading: true,
      areFiltersApplied: false,
      ...this.getDefaultFilteringOptions(),
      showSaveCustomViewModal: false,
      customViewInputNameModal: '',
      customViewInputNameTitle: '',
      customViewInputNameError: '',
      showAlert: false,
      alertMessage: '',
      alertType: null,
      alertVisibleFor: null,
      customViewRadioInput: createNewCustomView,
      columns: copyColumns(props.model)
    }

    this.handleResponse = this.handleResponse.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onPageSizeChange = this.onPageSizeChange.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.onClearFilterCriteria = this.onClearFilterCriteria.bind(this)
    this.onShareView = this.onShareView.bind(this)
    this.onSortChange = this.onSortChange.bind(this)
    this.onSaveCustomView = this.onSaveCustomView.bind(this)
    this.onRefresh = this.onRefresh.bind(this)
    this.handleCustomViewCreateError = this.handleCustomViewCreateError.bind(this)
    this.handleCustomViewCreateSuccess = this.handleCustomViewCreateSuccess.bind(this)
    this.handleCustomViewUpdateSuccess = this.handleCustomViewUpdateSuccess.bind(this)
    this.applySelectedColumns = this.applySelectedColumns.bind(this)
  }

  getDefaultFilteringOptions () : { [field: string] : {}} {
    const { withFilters, withSearch, withSorting, withPagination } = this.props
    const filteringOptions = {}

    if (withFilters) {
      const filterSelectors = withFilters
      const emptyFilters = {}
      filterSelectors.forEach((selector: FilterSelector) => {
        if(selector.id === 'separator') return
        const payload = selector.type === 'radio' ? null : []
        Object.assign(emptyFilters, { [selector.id]: payload })
      })

      if(withSearch) {
        Object.assign(emptyFilters, { search: '' })
      }

      Object.assign(filteringOptions, { filters: emptyFilters })
    }

    if (withSorting) {
      Object.assign(filteringOptions, { sort: withSorting })
    }

    if (withPagination) {
      const { page, pageSize } = withPagination
      Object.assign(filteringOptions, { pagination: { page, pageSize } })
    }

    return filteringOptions
  }

  applyViewFromStorage () {
    if (!window.sessionStorage.getItem(this.STORAGE_KEY)) {
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
    } else {
      const criteria = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      const areFiltersApplied = JSON.stringify(criteria.filters) !== JSON.stringify(this.getDefaultFilteringOptions().filters)
      this.setState({
        filters: criteria.filters,
        pagination: criteria.pagination,
        sort: criteria.sort,
        ...{ areFiltersApplied }
      })
      if (criteria.columns) this.setState({ columns: this.getColumnsFromView(criteria.columns) })
    }
  }

  static getCustomViewId () {
    return parseInt(window.location.pathname.split('/overview/')[1])
  }

  static getSharedView () {
    const search = qs.parse(window.location.search, { ignoreQueryPrefix: true })
    const view = search?.view

    try {
      return JSON.parse(window.atob(view))
    } catch (e) {
      return null
    }
  }

  static isCustomView (): boolean {
    return !!Overview.getCustomViewId()
  }

  static isSharedView (): boolean {
    return !!Overview.getSharedView()
  }

  async getView () {
    const customViewId = Overview.getCustomViewId()
    if (customViewId) {
      await this.fetchCustomView(customViewId)
      return
    }

    const sharedView = Overview.getSharedView()
    if (sharedView) {
      this.setSharedView(sharedView)
      return
    }

    this.applyViewFromStorage()
  }

  async componentDidMount () {
    await this.getView()
    await this.fetch()
  }

  async fetchCustomView (viewId: number) {
    await axios.get(`/view/${viewId}`)
      .then(res => this.setCustomView(res, viewId))
      .catch(() => {})
  }

  async fetch () {
    this.setState({ loading: true })
    const { endpoint } = this.props
    const { pagination, sort, filters } = this.state

    const dynamicFields = {}

    if (filters?.assignedModerator) {
      Object.assign(dynamicFields, { assignedModerator: filters?.assignedModerator.map((filter: { value: string }) => filter.value) })
    }

    if (filters?.author) {
      Object.assign(dynamicFields, { author: filters?.author.map((filter: { value: string }) => filter.value) })
    }

    if (filters?.userTag) {
      Object.assign(dynamicFields, { userTag: filters?.userTag.map((filter: { value: string }) => filter.value) })
    }

    await axios.post(endpoint, {
      pagination,
      sort,
      filters: {...filters, ...dynamicFields}
    })
      .then(res => this.handleResponse(res))
      .catch(() => this.setState({ loading: false }))
  }

  async createCustomView () {
    const { customViewInputNameModal, filters, sort, columns } = this.state
    const data = {
      name: customViewInputNameModal,
      criteria: {
        filters: filters,
        sort: sort,
        columns: this.getColumnsForView(columns)
      }
    }

    await axios.put('/view', data)
      .then(this.handleCustomViewCreateSuccess)
      .catch(this.handleCustomViewCreateError)
  }

  async updateCustomView () {
    const { customViewId, customViewInputNameTitle, filters, sort, columns } = this.state
    const data = {
      name: customViewInputNameTitle,
      criteria: {
        filters: filters,
        sort: sort,
        columns: this.getColumnsForView(columns)
      }
    }

    await axios.post(`/view/${customViewId}`, data)
      .then(this.handleCustomViewUpdateSuccess)
      .catch(() => this.showSomethingWentWrong())
  }

  onPageChange (page: number) {
    const pageSize = this.state.pagination?.pageSize
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onPageSizeChange (pageSize: number) {
    const page = this.props.withPagination?.page
    this.updateFilterCriteria('pagination', { page, pageSize })
  }

  onSortChange (order: string) {
    const { sort } = this.state

    const currentOrder = sort && Object.keys(sort).filter(currentOrder => currentOrder === order)

    if(currentOrder && currentOrder.length === 0) {
      this.setState({ sort: { [order]: 'asc' }})
    }

    const direction = this.state.sort?.[order]

    if (direction === 'desc') this.updateFilterCriteria('sort', { [order]: 'asc' })
    if (direction === 'asc') this.updateFilterCriteria('sort', { [order]: 'desc' })
  }

  onFilterChange (filter: string, payload: Array<string> | string | null) {
    const { filters } = this.state
    this.updateFilterCriteria('filters', {...filters, ...{ [filter]: payload }})
    // always to to the first page when updating filters
    const pagination = { page: 1, pageSize: this.state.pagination!.pageSize }
    this.setState({ areFiltersApplied: true, pagination })
  }

  onClearFilterCriteria () {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, () => {
        this.fetch()
      })
    } else {
      this.setState({ ...this.getDefaultFilteringOptions(), areFiltersApplied: false }, () => {
        this.fetch().then(() => {
          window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.getDefaultFilteringOptions()))
        })
      })
    }
  }
  onRefresh() {
    this.onPageChange(1)
  }

  onShareView () {
    const sharableViewUrl = this.getSharableViewUrl()

    copyToClipboard(sharableViewUrl)

    this.setState({
      showAlert: true,
      alertMessage: 'Link copied to clipboard!',
      alertType: 'success',
      alertVisibleFor: 3000
    })
  }

  async onSaveCustomView () {
    if (this.isCreateCustomView()) {
      this.validateCustomViewModalName()
      if (this.state.customViewInputNameError) {
        return
      }
      await this.createCustomView()
    } else {
      this.validateCustomViewTitleName()
      if (this.state.alertType === 'error') {
        return
      }
      await this.updateCustomView()
    }
  }

  updateFilterCriteria (key: string, value: {}) {
    if (Overview.isCustomView() || Overview.isSharedView()) {
      this.setState({ [key]: value }, () => {
        this.fetch()
      })
    } else {
      let storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
      storage[key] = value
      window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
      this.setState({ [key]: storage[key] }, () => {
        this.fetch()
      })
    }
  }

  handleResponse (response: AxiosResponse) {
    const { total } = response.data
    const data = response.data[this.props.dataKey]

    this.setState({
      data: data || [],
      total: total || undefined,
      loading: false
    })
  }

  handleCustomViewCreateSuccess (response: AxiosResponse) {
    const { data } = response

    if (data === undefined) {
      return
    }

    this.redirectToCustomView(data.viewId)
  }

  handleCustomViewUpdateSuccess (response: AxiosResponse) {
    this.redirectToCustomView(this.state.customViewId as number)
  }

  redirectToCustomView (viewId: number) {
    window.location.assign(`/overview/${viewId}?showMessage=success`)
  }

  handleCustomViewCreateError (error: AxiosError) {
    if (error.response && error.response.status === 422) {
      this.setState({ customViewInputNameError: 'This custom view name already exists' })
      return
    }

    this.showSomethingWentWrong()
  }

  showSomethingWentWrong () {
    this.setState({
      showAlert: true,
      alertMessage: 'Something went wrong. Please try again',
      alertType: 'error'
    })
  }

  getColumnsFromView(viewColumns: ViewColumn[]) {
    return this.state.columns.map((column) => {
      let viewColumn = viewColumns.find((viewColumn: ViewColumn) => column.accessor === viewColumn.accessor)
      if (viewColumn) column.isHidden = !!viewColumn.isHidden
      return column
    })
  }

  getColumnsForView(columns: Model[]) {
    return columns.map((column) => {
      return { accessor: column.accessor, isHidden: !!column.isHidden }
    })
  }

  setCustomView (response: AxiosResponse, customViewId: number) {
    if (response.data.length === 0) {
      return
    }

    const { name, criteria } = response.data
    const { filters, sort, columns } = criteria

    this.setState({
      customViewId,
      filters: {...this.state.filters, ...filters },
      sort,
      areFiltersApplied: true,
      customViewRadioInput: updateExistingCustomView,
      customViewInputNameTitle: name
    })
    columns && this.setState({ columns: this.getColumnsFromView(columns) })

    if (window.location.search.includes('?showMessage=success')) {
      this.setState({
        showAlert: true,
        alertMessage: `Custom view ‘${name}’ saved successfully!`,
        alertType: 'success',
        alertVisibleFor: 3000
      })
      window.history.replaceState(null, '', customViewId.toString())
    }

    this.trackCustomViewOpened()
  }

  setSharedView (sharedView: {filters: Filters, sort: Sort, columns: ViewColumn[]}) {
    const { filters, sort, columns } = sharedView

    this.setState({
      filters: filters,
      sort: sort
    })
    columns && this.setState({ columns: this.getColumnsFromView(columns) })
  }

  getSharableViewUrl (): string {
    const { filters, sort, columns } = this.state

    const view = window.btoa(JSON.stringify({
      filters: filters,
      sort: sort,
      columns: this.getColumnsForView(columns)
    }))

    return `${window.location.origin}/overview?view=${view}`
  }

  getPaginationProps () {
    const { withPagination } = this.props
    if(!withPagination) return

    const { pagination, total } = this.state

    return {
      pagination: Object.assign({},  pagination, { total }),
      onPageChange: this.onPageChange,
      onPageSizeChange: this.onPageSizeChange
    }
  }

  getTableProps () {
    const { withPagination, withSorting, withColumns, model, rowId } = this.props
    const { data, columns } = this.state

    const tableProps = {
      model: columns,
      rows: data,
      rowId,
      firstColumnSticky: true,
      actionElements: <div className='overview_table-action-elements'>
        <ButtonControl
            newStyle
            class='refresh-button'
            onClick={this.onRefresh} size='medium'
            title={<div><Icon icon='refresh' height={12} width={12} />Refresh</div>}
        />
        { withColumns && <OverviewColumns
            selectedColumns={columns}
            defaultColumns={model}
            applySelectedColumns={this.applySelectedColumns}
        /> }
      </div>
    }

    if (withSorting) {
      const { sort } = this.state
      sort && Object.keys(sort).forEach((key: string) => {
        Object.assign(tableProps, { sort: { order: key, direction: sort[key] }, onSortChange: this.onSortChange })
      })
    }

    if (withPagination) {
      Object.assign(tableProps, { withPagination: this.getPaginationProps()})
    }

    return tableProps
  }

  applySelectedColumns (columns: Model[]) {
    this.setState({ columns: columns })

    if (Overview.isCustomView() || Overview.isSharedView()) {
      return
    }

    let storage = JSON.parse(window.sessionStorage.getItem(this.STORAGE_KEY) as string)
    storage.columns = this.getColumnsForView(columns)
    window.sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(storage))
  }

  isCreateCustomView () {
    return this.state.customViewRadioInput === createNewCustomView
  }

  validateCustomViewModalName () {
    const { customViewInputNameModal } = this.state

    if (!customViewInputNameModal) {
      this.setState({ customViewInputNameError:
            'Please enter a name for your custom view' })
      return
    }

    if (customViewInputNameModal.length > 250) {
      this.setState({ customViewInputNameError:
            'Maximum length: 250 characters. Please reduce the length of the name of your custom view' })
      return
    }

    this.setState({ customViewInputNameError: '' })
  }

  validateCustomViewTitleName () {
    const { customViewInputNameTitle } = this.state

    if (!customViewInputNameTitle) {
      this.setState({
        showAlert: true,
        alertType: 'error',
        alertMessage: 'Please enter a name for your custom view',
        showSaveCustomViewModal: false
      })
      return
    }
  }

  trackCustomViewOpened () {
    tracker.trackEvent('userevent', {
      type: 'Custom View Opened',
      new_overview: true
    })
  }

  render ({
    type,
    headerTitle,
    headerButtons,
    withSearch,
    searchPlaceholder,
    withPagination,
    withFilters,
    rowId,
    model,
    EmptyStateComponent
  }: Props, {
    data,
    total,
    filters,
    columns,
    loading,
    areFiltersApplied,
    sort,
    showSaveCustomViewModal,
    customViewInputNameModal,
    customViewInputNameError,
    customViewInputNameTitle,
    showAlert,
    alertMessage,
    alertType,
    alertVisibleFor,
    customViewId,
    customViewRadioInput
  }: State) {

    return <div class='overview'>
      {loading && <Loader />}
      {showAlert && <AlertComponent
        message={alertMessage}
        messageType={alertType}
        isVisibleFor={alertVisibleFor}
        sticky
        onTimeoutEnd={() => this.setState({ showAlert: false })}
      />}
      <Header type={type}
        title={ !!customViewId ? customViewInputNameTitle : headerTitle }
        isCustomView={!!customViewId}
        onTitleChanged={(title: string) => this.setState({ customViewInputNameTitle: title})}
        onSaveViewClicked={() => this.setState({ showSaveCustomViewModal: true })}
        onShareView={this.onShareView}
        customButtons={headerButtons}
      />
      { withFilters && (data.length > 0 || areFiltersApplied || Overview.isSharedView()) && <OverviewFilters
        filters={withFilters}
        appliedFilters={filters}
        onFilterChange={this.onFilterChange}
        onClearFilterCriteria={this.onClearFilterCriteria}
        withSearch={withSearch}
        searchPlaceholder={searchPlaceholder}
      /> }
      {data.length > 0
        ? <Table {...this.getTableProps()} />
        : (!loading && (areFiltersApplied || Overview.isSharedView()))
          ? <div>
            <EmptyPage
              img_src={`${this.defaultCdn}/images/empty_result.png`}
              header='No results found'
              text={'Sorry, that filter combination has no results.\n Please try different criteria.'}
            />
          </div> : !loading && EmptyStateComponent
      }
      <SaveCustomViewModal
        customViewName={customViewInputNameModal}
        customViewRadioInput={customViewRadioInput}
        isCustomView={!!customViewId}
        onClose={() => this.setState({ showSaveCustomViewModal: false })}
        onCustomViewNameChanged={(e: ChangeEvent) => this.setState({ customViewInputNameModal: e.target.value })}
        onRadioButtonChanged={(option) => this.setState({ customViewRadioInput: option })}
        onSaveCustomView={this.onSaveCustomView}
        radioOptions={[updateExistingCustomView, createNewCustomView]}
        showModal={showSaveCustomViewModal}
        errorMessage={customViewInputNameError}
      />
    </div>
  }
}

export default Overview
